// current-user.js — the PURE currentUser derivation core (W5 polish).
//
// Extracted verbatim from opd-app/src/lib/medplum-client.js so Phase 2's Expo
// app reuses the real derivation instead of reimplementing it. Everything here
// takes the logged-in user's MedplumClient as an argument (RLS is the only lock
// — AGENTS.md §2) and touches NO module state, NO DOM, NO storage.
//
// What deliberately did NOT move: deriveCurrentUser() itself. That is a ~17-line
// memo wrapper around a module-level `currentUserCache` which is invalidated from
// three other places in the web client (onMedplumUnauthenticated, the login path,
// signOutMedplum) and which throws the app's NotLoggedInError. The cache is a
// per-session invariant of the HOST, not of the derivation — a signed-out or
// re-logged-in principal must never keep serving the previous user's role. So the
// host keeps the wrapper + the cache + its invalidation sites, and calls
// computeCurrentUser() below. Phase 2 supplies its own session memo.

const DEPT_SLUG_SYSTEM = 'https://healther.io/opd/department-slug';
const MUST_CHANGE_PWD_EXT = 'https://healther.io/opd/must-change-password';
// Seniority coding on the caller's OWN PractitionerRole.code (written by the
// admin-staff-invite Bot). Code 'hod' = Head of Department — the seniority the
// Protocols management UI + the manage-protocol write-auth gate on.
const SENIORITY_SYSTEM = 'https://healther.io/opd/seniority';
// The sibling GRADE coding (1|2|3) that refines the JR/SR bands into the fine
// 8-tier designation ladder (JR1…SR3 + Associate Professor + HOD). See
// medplum-designation.js for the full model.
const SENIORITY_GRADE_SYSTEM = 'https://healther.io/opd/seniority-grade';

// ── currentUser derivation ──────────────────────────────────────────────────
// The static cfg.currentUser is gone; role/department/name are derived from what
// Medplum ACTUALLY returns for the live session:
//   role: the SOLE source is auth/me's top-level effective AccessPolicy.basedOn[].display
//     → the canonical opd-* role (ROLE_BY_POLICY_PREFIX). Exactly one canonical policy
//     must match; a missing or ambiguous effective policy THROWS — there is no
//     Practitioner-extension / membership-policy-name / seed-shape fallback chain any
//     more. Human admin/owner memberships are admin:false; only privileged Bots are
//     Medplum project administrators. The one exception is the bedside DEVICE identity,
//     which short-circuits to role 'bedside' off its profile identifier (its auth/me
//     summary carries no access[] to resolve a policy from — see BEDSIDE_DEVICE_ID_SYSTEM).
//     Practitioner.extension https://healther.io/opd/role is a roster/display mirror
//     only (opd-admin can edit Practitioner) and never participates in authorization here.
//   department: PractitionerRole.organization (seed doctors) or the
//     membership's department parameter (Bot-invited doctors/nurses), resolved
//     to the dept Organization → its department-slug identifier value.
const ROLE_BY_POLICY_PREFIX = [
  // 'opd-intern' MUST precede 'opd-doctor' is unnecessary (names are distinct), but
  // interns derive role 'intern' from opd-intern-rowlevel — a trainee/visiting tier
  // that works up patients but cannot prescribe/sign (owner decision 2026-07-19).
  ['opd-intern', 'intern'],
  ['opd-doctor', 'doctor'],
  ['opd-nurse', 'nurse'],
  ['opd-receptionist', 'receptionist'],
  ['opd-admin', 'admin'],
  ['opd-owner', 'owner'],
  // Dedicated bedside-device account (opd-bedside AccessPolicy, glance-only RLS).
  // A tablet signed in as this identity derives role 'bedside'.
  ['opd-bedside', 'bedside'],
];
// The dedicated bedside-device identity (provision-bedside-device.mjs) stamps this
// identifier system on its Practitioner profile. It is the RELIABLE, admin-free
// signal for role 'bedside': a bedside token's own auth/me summary omits
// membership.access[] entirely (verified live on Medplum 5.1.x — the non-admin
// membership read is project-admin-only), so the ROLE_BY_POLICY_PREFIX 'opd-bedside'
// mapping below can never fire for the device, and deriveRole's seed-shape fallback
// would otherwise mis-derive it as 'receptionist'. The profile identifier, by
// contrast, a user can always read on its own Practitioner (extensions/identifiers
// intact), so we key role derivation on it directly.
const BEDSIDE_DEVICE_ID_SYSTEM = 'https://healther.tech/bedside-device';
function isBedsideDevice(profile) {
  return profile?.resourceType === 'Practitioner'
    && ((profile.identifier || []).some((i) => i && i.system === BEDSIDE_DEVICE_ID_SYSTEM));
}

/**
 * Derives { id, name, displayName, role, department, departmentRef, seniority,
 * isHOD, designation, mustChangePassword } — the exact DTO shape the old static
 * cfg.currentUser had — from the caller's own live Medplum session. `medplum` is
 * the LOGGED-IN USER's client and `profile` its already-resolved profile
 * resource (the host reads it via getProfileAsync() and owns the memo).
 */
export async function computeCurrentUser(medplum, profile) {
  const membership = medplum.getProjectMembership(); // summary only; protected fields may be stripped
  const effectiveAccessPolicy = medplum.getAccessPolicy(); // auth/me server-issued effective policy
  const n = (profile.name && profile.name[0]) || undefined;
  const name = (n && (n.text || [...(n.given || []), n.family].filter(Boolean).join(' '))) || '';

  // Every staff policy grants PractitionerRole read (no criteria) — a failure
  // here is a real policy regression and should surface, not be swallowed.
  let practRole;
  if (profile.resourceType === 'Practitioner') {
    practRole = await medplum.searchOne('PractitionerRole',
      'practitioner=Practitioner/' + profile.id + '&active=true');
  }

  // Bedside device short-circuits to role 'bedside' off its profile identifier
  // (see BEDSIDE_DEVICE_ID_SYSTEM) — its auth/me summary carries no access[] to
  // resolve the policy name from, so the prefix mapping below can't reach it.
  const role = isBedsideDevice(profile)
    ? 'bedside'
    : deriveRole(effectiveAccessPolicy);
  // The device has no department Organization (project-global glance) — skip the
  // dept resolution (which would 403 on the excluded reads anyway) and report null.
  const department = role === 'bedside' ? null : await deriveDepartment(medplum, membership, practRole);
  // Seniority (jr-resident | sr-resident | associate-prof | hod) off the caller's
  // OWN PractitionerRole.code (the admin-staff-invite Bot writes it there). isHOD is
  // the boolean the Protocols management UI gates on. Both are '' / false for staff
  // without a seniority coding (every non-HOD doctor today) — additive, never blocks.
  const { seniority, isHOD } = deriveSeniority(practRole);
  // The FINE 8-tier designation (band + optional grade) off the SAME PractitionerRole,
  // as { band, grade, key, short, label, rank } — or null when the role carries no
  // seniority band (admin/reception, or a doctor with no designation assigned yet).
  // The avatar menu renders `short` as a chip and `label` as its tooltip.
  const designation = deriveDesignation(practRole);
  // The department Organization reference (Organization/<uuid>) — the management UI
  // needs the REF (not just the slug) to stamp a new protocol's owning-department.
  const departmentRef = deriveDepartmentRef(membership, practRole);
  // The forced-password-change marker: read it off the caller's OWN Practitioner
  // profile (in hand from auth/me — a user can always read its own profile, on
  // any server version, extensions intact). The invite Bot mirrors the marker
  // there for exactly this reason (the ProjectMembership copy is admin-only and
  // invisible to the non-admin who must act on it). The membership copy is still
  // honoured as a fallback for admin logins (which CAN read their membership),
  // so an admin invited with a temp password is forced to change too.
  const hasMarker = (res) => ((res && res.extension) || []).some(
    (e) => e.url === MUST_CHANGE_PWD_EXT && e.valueBoolean === true,
  );
  const mustChangePassword = hasMarker(profile) || hasMarker(membership);
  // Doctor honorific for display parity with the old static DTO
  // ('Dr Asha Verma'); the Practitioner name itself carries no prefix.
  const displayName = role === 'doctor' && name && !/^dr\.?\s/i.test(name) ? 'Dr ' + name : name;
  return { id: profile.id, name, displayName, role, department, departmentRef, seniority, isHOD, designation, mustChangePassword };
}

// Seniority off the caller's own PractitionerRole.code seniority coding. Returns
// { seniority:'', isHOD:false } when absent (the common case today). We read the
// coding the admin-staff-invite Bot writes (system SENIORITY_SYSTEM); code 'hod'
// is the only one the protocols surface treats specially.
function deriveSeniority(practRole) {
  for (const cc of (practRole && practRole.code) || []) {
    for (const c of (cc.coding || [])) {
      if (c.system === SENIORITY_SYSTEM && c.code) {
        const code = String(c.code).toLowerCase();
        return { seniority: code, isHOD: code === 'hod' };
      }
    }
  }
  return { seniority: '', isHOD: false };
}

// The FINE 8-tier designation off the caller's own PractitionerRole.code — the
// coarse band coding (SENIORITY_SYSTEM) plus, for the JR/SR bands, the sibling
// grade coding (SENIORITY_GRADE_SYSTEM). Returns { band, grade, key, short, label,
// rank } or null when no band is present.
//
// INLINED on purpose: this is a compact copy of designationOfRole()/parseDesignation()
// in medplum-designation.js. That module imports getMedplum() from the app's
// medplum-client.js, which imports THIS module, so importing its reader back here
// would create an import cycle (forbidden — AGENTS §1).
// The two must agree; the designation vitest covers the tier→short/label mapping.
const DESIGNATION_BAND_BASE = { 'jr-resident': 10, 'sr-resident': 20, 'associate-prof': 30, hod: 40 };
function normDesignationBand(v) {
  const t = String(v || '').trim().toLowerCase();
  if (t === 'jr' || t === 'jr-resident' || t === 'junior' || t === 'junior-resident') return 'jr-resident';
  if (t === 'sr' || t === 'sr-resident' || t === 'senior' || t === 'senior-resident') return 'sr-resident';
  if (t === 'associate-prof' || t === 'associate' || t === 'assoc' || t === 'associate-professor' || t === 'ap') return 'associate-prof';
  if (t === 'hod' || t === 'head' || t === 'head-of-department') return 'hod';
  return '';
}
function normDesignationGrade(v) {
  const n = parseInt(String(v == null ? '' : v).trim(), 10);
  return (n === 1 || n === 2 || n === 3) ? n : 0;
}
function deriveDesignation(practRole) {
  let bandCode = '';
  let gradeCode = 0;
  for (const cc of (practRole && practRole.code) || []) {
    for (const c of (cc.coding || [])) {
      if (c && c.system === SENIORITY_SYSTEM && c.code) bandCode = String(c.code).toLowerCase();
      if (c && c.system === SENIORITY_GRADE_SYSTEM && c.code) gradeCode = normDesignationGrade(c.code) || gradeCode;
    }
  }
  const band = normDesignationBand(bandCode);
  if (!band) return null;
  let grade = normDesignationGrade(gradeCode);
  if (band === 'associate-prof' || band === 'hod') grade = 0;
  const short = band === 'jr-resident' ? (grade ? `JR${grade}` : 'JR')
    : band === 'sr-resident' ? (grade ? `SR${grade}` : 'SR')
      : band === 'associate-prof' ? 'AP' : 'HOD';
  const label = band === 'jr-resident' ? `Junior Resident${grade ? ` ${grade}` : ''}`
    : band === 'sr-resident' ? `Senior Resident${grade ? ` ${grade}` : ''}`
      : band === 'associate-prof' ? 'Associate Professor' : 'Head of Department';
  const key = (band === 'jr-resident' || band === 'sr-resident') && grade
    ? (band === 'jr-resident' ? 'jr' : 'sr') + grade : band;
  const rank = DESIGNATION_BAND_BASE[band] + grade;
  return { band, grade, rank, key, short, label };
}

// The doctor's department Organization reference (Organization/<uuid>) — from the
// PractitionerRole.organization (seed doctors) or the membership's department
// parameter (Bot-invited doctors). Same resolution as deriveDepartment, but returns
// the REF the management UI stamps as owning-department, not the resolved slug.
function deriveDepartmentRef(membership, practRole) {
  const orgRef = (practRole && practRole.organization && practRole.organization.reference) || '';
  if (orgRef) return orgRef;
  for (const a of (membership && membership.access) || []) {
    const p = (a.parameter || []).find((x) =>
      x.name === 'department' && x.valueReference && x.valueReference.reference);
    if (p) return p.valueReference.reference;
  }
  return null;
}

function deriveRole(effectiveAccessPolicy) {
  const policyNames = (effectiveAccessPolicy?.basedOn || []).map((ref) => ref?.display).filter(Boolean);
  const policyRoles = new Set();
  for (const policyName of policyNames) {
    const normalized = String(policyName).trim().toLowerCase();
    for (const [prefix, role] of ROLE_BY_POLICY_PREFIX) {
      if (normalized === prefix || normalized.startsWith(prefix + '-')) policyRoles.add(role);
    }
  }
  if (policyRoles.size === 1) {
    return [...policyRoles][0];
  }
  if (policyRoles.size > 1) throw new Error('multiple canonical staff policies are active for this account');
  throw new Error('staff session is missing its canonical effective AccessPolicy role');
}

async function deriveDepartment(medplum, membership, practRole) {
  let orgRef = (practRole && practRole.organization && practRole.organization.reference) || '';
  if (!orgRef) {
    for (const a of (membership && membership.access) || []) {
      const p = (a.parameter || []).find((x) =>
        x.name === 'department' && x.valueReference && x.valueReference.reference);
      if (p) { orgRef = p.valueReference.reference; break; }
    }
  }
  if (!orgRef) return null; // receptionist/admin: project-global, no department
  const org = await medplum.readReference({ reference: orgRef });
  const slug = (org.identifier || []).find((i) => i.system === DEPT_SLUG_SYSTEM);
  return (slug && slug.value) || (org.name ? org.name.toLowerCase() : null);
}
